import type { AxiosRequestConfig } from 'axios';

import { getApiClient } from './client';

/**
 * Per-call request context.
 *
 * orval turns a second mutator parameter into
 * `options?: SecondParameter<typeof customInstance>` on every generated
 * function and a `request?:` key on every generated hook, which is the only way
 * a value that varies per call can reach the request. Web needs exactly that:
 * it holds one guest token per roll, so no interceptor can pick the right one.
 * Mobile holds a single guest token globally and sets it in an interceptor, so
 * it never passes this.
 *
 * `guestToken` is required rather than optional on purpose. orval inserts the
 * new parameter *before* the trailing `signal?: AbortSignal`, and an object
 * whose properties were all optional would be structurally satisfied by an
 * `AbortSignal` — every call site still passing a signal positionally would
 * bind it to this parameter and keep compiling. With a required property those
 * call sites are type errors instead.
 */
export interface RequestOptions {
  /** Sent as `X-Guest-Token` on this one request. */
  guestToken: string;
}

export const customInstance = async <T>(
  config: AxiosRequestConfig,
  options?: RequestOptions,
): Promise<T> => {
  const { data } = await getApiClient().request<T>({
    ...config,
    headers: options
      ? { ...config.headers, 'X-Guest-Token': options.guestToken }
      : config.headers,
  });
  return data;
};

export default customInstance;
